import { NgClass } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  forwardRef,
  Injector,
  input,
  Input,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  FileItem,
  FileLikeObject,
  FileUploader,
  FileUploadModule,
} from 'ng2-file-upload'
import { InfoMessageComponent } from '@wm-front/shared/common-ui'

declare const $localize: any

@Component({
  selector: 'wm-front-input-upload',
  templateUrl: './input-upload.component.html',
  styleUrls: ['./input-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => InputUploadComponent),
      multi: true,
    },
  ],
  imports: [NgClass, FileUploadModule, MatIconModule, MatCheckboxModule],
})
export class InputUploadComponent implements OnInit, ControlValueAccessor {
  /** Element reference to hidden file input  */
  @ViewChild('fileinput') public fileinput: ElementRef

  /** Placeholder for empty dropzone */
  @Input() public placeholder: string

  /** Whether we allow multiple uploads */
  @Input() public multiple: boolean

  /** The files which are accepted */
  @Input() public acceptedFiles: string[] = ['application/pdf']

  /** File to set for edition for instance */
  public filesToSet = input<File[] | undefined>()

  /** The files to display in the upload window */
  public accept: string

  /** Uploader */
  public uploader: FileUploader

  /** File currently dragged over upload box */
  public hasBaseDropZoneOver: boolean

  /** Whether there is a wrong file type error */
  public wrongFileTypeError: boolean

  protected _cd = inject(ChangeDetectorRef)

  protected _snackbar = inject(MatSnackBar)

  protected _injector = inject(Injector)

  /** Inner value */
  private innerValue: File | File[]

  /** Get file in queue */
  public get file(): FileLikeObject {
    return this.uploader.queue[0]?.file
  }

  /** Get file in queue */
  public get files(): FileItem[] {
    return this.uploader.queue
  }

  public get value(): File | File[] | undefined {
    return this.innerValue
  }

  /** Set value of form input */
  public set value(value: File | File[] | undefined) {
    if (value !== undefined) {
      this.onChanged(value)
      this.onTouched(value)
      this.innerValue = value
    }
  }

  public ngOnInit(): void {
    this.uploader = new FileUploader({
      url: '',
      allowedMimeType: this.acceptedFiles,
    })

    this.hasBaseDropZoneOver = false
    this.setFileAccepted()
    this.manageFilesToSet()
  }

  /** Update value of control accessor
   * @param { File[] }
   */
  public updateValue(files: File[]): void {
    if (this.multiple) {
      let duplicateReportsAmount = 0
      /* eslint-disable */
      for (let i = 0; i < files.length; i++) {
        if (this.isDuplicate(files[i])) {
          duplicateReportsAmount++

          continue
        }

        if (!this.acceptedFiles.includes(files[i].type)) {
          this.wrongFileTypeError = true
          setTimeout(() => {
            this.wrongFileTypeError = false
            this._cd.markForCheck()
          }, 3000)
        } else {
          this.value = [...((this.value || []) as File[]), files[i]] as File[]
        }
        /* eslint-enable */
      }

      if (duplicateReportsAmount === 1) {
        this._snackbar.openFromComponent(InfoMessageComponent, {
          data: {
            text: $localize`:Toaster label | File upload duplicate file name@@inputUploadComponentDuplicateFileNameToasterLabel:1 rapport non importé (doublon)`,
          },
          duration: 3000,
        })
      } else if (duplicateReportsAmount > 1) {
        this._snackbar.openFromComponent(InfoMessageComponent, {
          data: {
            text: $localize`:Toaster label | File upload duplicate file name@@inputUploadComponentDuplicateFileNamesToasterLabel:${duplicateReportsAmount} rapports non importés (doublon)`,
          },
          duration: 3000,
        })
      }
    } else {
      if (!this.acceptedFiles.includes(files[0].type)) {
        this.wrongFileTypeError = true
        setTimeout(() => {
          this.wrongFileTypeError = false
          this._cd.markForCheck()
        }, 3000)
      }

      this.value = files[0]
    }
  }

  /** Remove file from list by index
   * @param { number }
   */
  public remove(i: number): void {
    this.uploader.queue[i].remove()
    this.value = (this.value as File[]).filter((file, index) => index !== i)
  }

  /** Init value function given by the implementation of controlValueAccessor */
  public writeValue(newValue: File | File[]): void {
    if (newValue) {
      this.value = newValue
    } else {
      this.value = undefined
    }
  }

  /** Handle pristine behaviour */
  public registerOnChange(fn: () => void): void {
    this.onChanged = fn
  }

  /** Handle blur behaviour */
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  /** Event when file hovered over upload box */
  public fileOverBase(e: boolean): void {
    this.hasBaseDropZoneOver = e
  }

  /** Trigger file explorer opening through hidden file input */
  public triggerFileInput(): void {
    this.fileinput.nativeElement.click()
  }

  /** Clear uploader queue */
  public clearQueue(): void {
    this.uploader.clearQueue()
  }

  /**
   * Will set which file is accepted to be displayed in the window
   * @private
   */
  private setFileAccepted(): void {
    this.accept = this.acceptedFiles.reduce(
      (previous: string, currentValue: string) =>
        `${previous}${previous ? ', ' : ''}${currentValue}`,
      ''
    )
  }

  /**
   * Will know what to do when a file is set
   * @private
   */
  private manageFilesToSet(): void {
    effect(
      () => {
        if (this.filesToSet()?.length) {
          this.updateValue(this.filesToSet() as File[])
        }
      },
      { injector: this._injector }
    )
  }

  private isDuplicate(file: File): boolean {
    return !!((this.value || []) as File[]).find(
      (searchedFile) => searchedFile.name === file.name
    )
  }

  /** Handle pristine behaviour */
  private onChanged: (file: File | File[]) => void = () => undefined

  /** Handle touch behaviour */
  private onTouched: (file: File | File[]) => void = () => undefined
}
